import Oidc from 'oidc-client';
import axios from 'axios';
import UsersService from '@/services/UsersService';
import { changeLocalization } from '@/i18n';
import AppConfig from '@/mixins/AppConfig-APP_TARGET';

export default new class AuthenticationService {

  userManager
  isAuthenticated = false
  isAuthenticatedExternal = false
  userProfile
  localUserProfile
  redirectUri
  isExternalIdentityProvider

  getUser() {
    return this.UserManager.getUser().then(user => {
      if (user) {
        this.userProfile = user.profile;
        this.isExternalIdentityProvider = this.userProfile?.idp != null && this.userProfile.idp != 'oidc-msmloop' && this.userProfile.idp != 'local';
        axios.defaults.headers.Authorization = "Bearer " + user.access_token;

        // Get user profile data from backend
        return UsersService.getUserProfile().then(response => {
          if (response.data) {
            this.isAuthenticated = true;
            this.localUserProfile = response.data;

            return changeLocalization(this.localUserProfile.LocalizationId,
              // Don't use client specific localizations for msm users
              !this.isMSMUser ? this.localUserProfile.ClientId : null);
          } else {
            return this.UserManager.removeUser().then(() => {
              return Promise.reject('unauthorized');
            });
          }
        }).catch((e) => {
          // If backend returns 401, the access token is probably expired --> sign in again
          if (e && e.response && e.response.status == 401)
            return this.UserManager.signoutRedirect();
          else
            return Promise.reject(e);
        });
      }
      else {
        this.isAuthenticated = false;
        this.userProfile = null;
        this.isExternalIdentityProvider = null;
      }
    });
  }

  login(params) {
    return this.getUser().then(() => {
      if (!this.isAuthenticated) {
        return this.UserManager.signinRedirect({
          ...params?.login_hint != null ? { login_hint: params.login_hint } : {},
          state: window.location.pathname + window.location.search,
          ...params?.idp_hint != null ? { extraQueryParams: { kc_idp_hint: params.idp_hint } } : {}
        });
      }
    });
  }

  callback() {
    return this.UserManager.signinRedirectCallback().then((user) => {
      this.redirectUri = user.state;
      return this.getUser();
    }).catch((e) => {
      this.isAuthenticated = false;
      this.userProfile = null;
      console.error(e);
      return Promise.reject(e);
    });
  }

  loginExternal(redirectTo, externalUser, remember) {
    if (!externalUser && this.$cookies.isKey("ExternalUser")) {
      externalUser = this.$cookies.get("ExternalUser");
    }

    if (externalUser)
      return UsersService.setExternalUser(externalUser).then(response => {
        if (response.data) {
          this.isAuthenticatedExternal = true;
          this.localUserProfile = response.data;

          if (remember)
            this.$cookies.set("ExternalUser", externalUser, Infinity);

          externalUser.UserId = this.localUserProfile.UserId;
          externalUser.SurveyInstanceCustomerCode = redirectTo.query.sicc;
          axios.defaults.headers.Authorization = "Simple " + Buffer.from(JSON.stringify(externalUser)).toString('base64');

          return changeLocalization(this.localUserProfile.LocalizationId);
        } else {
          return Promise.reject();
        }
      });
    else
      return Promise.reject();
  }

  logout() {
    if (!this.isExternalUser)
      return this.UserManager.signoutRedirect();
    else {
      this.isAuthenticated = false;
      this.isAuthenticatedExternal = false;
      this.$cookies.remove("ExternalUser");
      return Promise.resolve();
    }
  }

  get UserManager() {
    if (!this.userManager) {
      var config = {
        authority: process.env.VUE_APP_AUTH_SERVERURL,
        client_id: 'loopRepFrontend',
        redirect_uri: `${window.location.origin}${process.env.BASE_URL}callback`,
        response_type: 'id_token token',
        scope: 'openid profile email loopRepBackend',
        post_logout_redirect_uri: window.location.origin,
        silent_redirect_uri: `${window.location.origin}${process.env.BASE_URL}/silentrenew`,
        accessTokenExpiringNotificationTime: 10,
        automaticSilentRenew: true,
      };

      this.userManager = new Oidc.UserManager(config);
      this.userManager.events.addUserLoaded(user => {
        axios.defaults.headers.Authorization = "Bearer " + user.access_token
      })
      this.userManager.events.addSilentRenewError(() => {
        console.debug('Silent Renew Error');
      });
    }

    return this.userManager;
  }

  get isMSMUser() {
    return this.localUserProfile?.UserType === 'M';
  }

  get isExternalUser() {
    return this.localUserProfile?.UserType === 'E';
  }

  get authorizationHeader() {
    return { Authorization: axios.defaults.headers.Authorization };
  }

  get logoExternalImageUrl() {
    let retVal = process.env.BASE_URL + 'static/img/msm_loop_logo_40px.png';

    if (this.localUserProfile.LogoExternalImageId && (AppConfig.methods.isDemo() || !this.isMSMUser)) {
      retVal = process.env.BASE_URL + `api/media/image?id=${this.localUserProfile.LogoExternalImageId}`;
    }

    return retVal;
  }
}
